import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Play, Pause, SpeakerHigh, SpeakerX, ArrowLeft, ArrowRight } from "@phosphor-icons/react";

/**
 * LandingVideoExplainer — an auto-playing "video-like" slideshow rendered at
 * the top of the Landing page. Product spec called for AI-generated content:
 *
 *   • Slide text is loaded from the admin-editable `/api/landing/video-explainer`
 *     endpoint (Gemini-generated content can be swapped in later without a
 *     redeploy).
 *   • Each slide displays for 6 s, auto-advances, then loops back to slide 1.
 *   • Voice-over uses the browser's Web Speech API (SpeechSynthesis) — zero API
 *     cost, works on every modern browser. Muted by default so the page isn't
 *     noisy for cold visitors; a single click on the speaker icon starts audio.
 *   • Slide-1 backgrounds fall back to a branded gradient so the slot always
 *     paints something, even before an admin uploads Nano Banana images.
 */
const SLIDE_MS = 6000;
const GRADIENTS = [
  "from-blue-600 via-indigo-600 to-purple-700",
  "from-emerald-500 via-teal-500 to-cyan-600",
  "from-fuchsia-500 via-pink-500 to-rose-500",
  "from-amber-500 via-orange-500 to-red-500",
  "from-violet-600 via-indigo-500 to-blue-600",
];

export default function LandingVideoExplainer() {
  const [cfg, setCfg] = useState(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const timerRef = useRef(null);
  const utterRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/landing/video-explainer");
        setCfg(r.data);
      } catch { /* silent — landing still paints without the slot */ }
    })();
  }, []);

  // Auto-advance
  useEffect(() => {
    if (!cfg || !playing) return;
    timerRef.current = setTimeout(() => {
      setIdx((i) => (i + 1) % cfg.slides.length);
    }, SLIDE_MS);
    return () => clearTimeout(timerRef.current);
  }, [cfg, idx, playing]);

  // Voiceover
  useEffect(() => {
    if (!cfg || muted || !("speechSynthesis" in window)) return;
    const slide = cfg.slides[idx];
    if (!slide) return;
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    const utter = new SpeechSynthesisUtterance(`${slide.headline}. ${slide.body}`);
    utter.rate = 1.05;
    utter.pitch = 1.0;
    utter.volume = 1.0;
    utterRef.current = utter;
    try { window.speechSynthesis.speak(utter); } catch { /* ignore */ }
    return () => {
      try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    };
  }, [cfg, idx, muted]);

  if (!cfg) return null;
  const slide = cfg.slides[idx] || {};
  const bgImage = cfg.slide_images?.[idx];
  const gradient = GRADIENTS[idx % GRADIENTS.length];

  return (
    <section
      data-testid="landing-video-explainer"
      className="relative w-full max-w-6xl mx-auto rounded-2xl overflow-hidden border border-white/10 shadow-2xl mb-8"
      style={{ aspectRatio: "16 / 9" }}
    >
      {/* Background — Nano Banana image if admin uploaded, else brand gradient */}
      {bgImage ? (
        <img src={bgImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />
      )}
      <div className="absolute inset-0 bg-black/45" />

      {/* Slide content */}
      <div className="relative z-10 h-full flex flex-col justify-center p-6 md:p-12 max-w-2xl">
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-white/70 mb-2">
          {cfg.subtitle || "Tainapp"}
        </p>
        <h2
          key={`h-${idx}`}
          data-testid={`slide-headline-${idx}`}
          className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight animate-fade-in"
        >
          {slide.headline}
        </h2>
        <p
          key={`b-${idx}`}
          className="mt-3 text-white/85 text-base md:text-lg leading-relaxed animate-fade-in"
        >
          {slide.body}
        </p>
      </div>

      {/* Controls */}
      <div className="absolute bottom-3 left-3 right-3 z-20 flex items-center justify-between gap-2">
        <div className="flex gap-2">
          <button
            aria-label="Previous slide"
            data-testid="video-prev"
            onClick={() => setIdx((i) => (i - 1 + cfg.slides.length) % cfg.slides.length)}
            className="w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white"
          >
            <ArrowLeft size={16} weight="bold" />
          </button>
          <button
            aria-label={playing ? "Pause" : "Play"}
            data-testid="video-play-toggle"
            onClick={() => setPlaying((p) => !p)}
            className="w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white"
          >
            {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
          </button>
          <button
            aria-label="Next slide"
            data-testid="video-next"
            onClick={() => setIdx((i) => (i + 1) % cfg.slides.length)}
            className="w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white"
          >
            <ArrowRight size={16} weight="bold" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="flex gap-1.5" data-testid="video-progress">
          {cfg.slides.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "w-8 bg-white" : "w-4 bg-white/40 hover:bg-white/60"
              }`}
            />
          ))}
        </div>

        <button
          aria-label={muted ? "Unmute voiceover" : "Mute voiceover"}
          data-testid="video-mute-toggle"
          onClick={() => setMuted((m) => !m)}
          className="w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white"
        >
          {muted ? <SpeakerX size={16} weight="bold" /> : <SpeakerHigh size={16} weight="bold" />}
        </button>
      </div>
    </section>
  );
}
